import React, { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import classnames from "classnames";

import { useConfig } from "src/hooks/config";
import { useTranslation } from "src/hooks/translation";
import { useOutSideClick } from "src/hooks/outSideClick";
import ToggleSwitch from "src/common/components/toggleSwitch";
import mainStore, { storeSetIsProfitBoost } from "src/stores/main";

import "./style.scss";
import { BetCalculationsEnum, getBetCalculations } from "calculation";
import upperDataStore from "src/stores/upperData";
import selectionsStore from "src/stores/selections";

interface IProps {
  canUseProfitBoost: () => boolean
}

const ProfitBoost = ({ canUseProfitBoost }: IProps) => {
  const config = useConfig();
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { profitBoostPercent, profitBoostStake, isProfitBoost, stake } = useRecoilValue(mainStore);
  const containerRef = useOutSideClick(() => setIsOpen(false), []);
  const upperDataState = useRecoilValue(upperDataStore);

  const { usageRules } = config.wheelConfig || {}

  const { selections } = useRecoilValue(selectionsStore);
  const { partnerConfig, bonusRules, currencyConfig } = upperDataState;



  const { expressBonus } = useMemo(
    () =>
      getBetCalculations([BetCalculationsEnum.Bonus], {
        config,
        partnerConfig,
        stake: +stake,
        events: selections,
        bonusRule: bonusRules,
        currencyConfig
      }),
    [config, partnerConfig, stake, selections, bonusRules, currencyConfig]
  );

  const toggleProfitBoost = () => storeSetIsProfitBoost(!isProfitBoost);


  useEffect(() => {
    if (!canUseProfitBoost() && isProfitBoost) {
      setIsOpen(true)
      storeSetIsProfitBoost(false)
    }
  }, [canUseProfitBoost, isProfitBoost])

  const infoHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation()
    setIsOpen(!isOpen);
  };

  const className = classnames("B-p-b-container", {
    isActive: isProfitBoost,
    isDeactive: !isProfitBoost,
  });

  const usageMessage = useMemo(() => {
    if (!usageRules) return '';

    let message = `${t('To use this Profit Boost, your next bet must meet the following criteria')}:\n\n`

    if (usageRules.minStake) message+=`${t('Stake')}: Min ${usageRules.minStake} ${currencyConfig?.name}, Max ${(profitBoostStake)?.toString()} ${currencyConfig?.name}\n`
    else
      message += `${t('Stake')}: Max ${(profitBoostStake)?.toString()} ${currencyConfig?.name}\n`;

    message += `${t('Odds')}: Min ${usageRules?.minOdd.toString()}, Max ${usageRules?.maxOdd.toString()}\n${t('Selections count')}: Min ${usageRules?.minSelections.toString()}, Max ${usageRules?.maxSelections.toString()}`

    return message
  }, [usageRules, profitBoostStake])

  if (!usageRules) return null;

  return (
    <label className={className} htmlFor="toggle">
      <div className="B-p-b-leftSide" ref={containerRef}>
        <div className="B-p-b-info-container">
          <i className="global-icon icon-boost-gift" />
          <span className="B-p-b-txt">{t("Profit Boost")}</span>
          <span
            className={classnames("B-p-b-percent", {
              "B-p-b-active": isProfitBoost,
              "B-p-b-deActive": !isProfitBoost,
            })}
          >
            {profitBoostPercent}%
          </span>
        </div>
        <i className="global-icon icon-filled-info tooltip" onClick={e => infoHandler(e)} />
        {isOpen && config.wheelConfig && (
          <div className={`B-p-b-info ${expressBonus.amount ? 'B-p-b-info-bonus' : ''}`}>
            {expressBonus.amount ? t('Accumulator bonus is not combatable with profit boost.') : usageMessage}
          </div>
        )}
      </div>
      <div className="B-p-b-rightSide">
        <ToggleSwitch onChange={toggleProfitBoost} checked={isProfitBoost} id="toggle" />
      </div>
    </label>
  );
};

export default ProfitBoost;
